<template>
  <div class="filter-row" data-test-id="filter-row">
    <div>
      <slot name="filterSelector" />
    </div>

    <div>
      <ModeSelector :filter="filter" :mode="filter.value.mode" @select="setMode" />
    </div>

    <div>
      <DebounceUpdate v-if="valueType === 'text'" @update="setValue" :debounce-time="300" v-slot="{ debounced }">
        <InputBox
          :modelValue="filter.value.value || ''"
          focus-on-mount
          @update:modelValue="debounced"
          blur-on-enter-press
        />
      </DebounceUpdate>

      <SelectPopover
        v-else-if="valueType === 'id-list'"
        :type="useSingleSelect ? 'single' : 'multi'"
        size="fixed"
        :search="filter.getText"
        :label="filter.name"
        :placeholder="texts.placeholder"
        :empty-text="texts.empty"
        :search-empty-text="texts.searchEmpty"
        :options="itemOptions"
        :option-groups="optionGroups"
        :modelValue="value"
        mode="apply"
        :is-disabled="isDisabled"
        clickable-option="custom-control"
        @apply="(value) => (useSingleSelect ? setValue([value]) : setValue(value))"
      >
        <template #button-content>
          <FilterValueDisplay
            v-if="hasSelectedItems"
            :selectedItems="selectedItems"
            :buttonComponent="buttonComponent"
            :filterName="filter.name"
            class="filter-row__list"
          />
          <template v-else>
            <span>
              {{ texts.placeholder }}
            </span>
            <ChevronDownIcon16 />
          </template>
        </template>

        <template #default="{ label, isSelected, isFocussed, isHighlighted, disabled, toggleSelection }">
          <div class="data-picker__content">
            <component
              :is="selectComponent"
              :is-selected="isSelected"
              :primary-text="getDisplayText ? getDisplayText(label) : label.name || emptyLabel"
              :is-focussed="isFocussed"
              :is-highlighted="isHighlighted"
              :is-disabled="Boolean(disabled)"
              :control-style="useSingleSelect ? 'radio' : 'toggle'"
              @toggle="toggleSelection"
            >
              <template #left>
                <component :is="itemComponent" v-if="itemComponent !== 'CodeSelectItem'" />
              </template>

              <!-- CodeSelectItem uses these two slots -->
              <template #secondary-action>
                <ColorPickerButton @click="editCode(label)" :color="label.color" />
              </template>

              <template #supplement>
                <CodeOccurrenceBubble
                  :occurrence="label.occurrence"
                  :tooltip="`${$t('prop.occurrence')}: ${label.occurrence}`"
                />
              </template>
            </component>
          </div>
        </template>

        <!-- <component :is="itemComponent" slot-scope="{ label }" :item="label" :selectable="false" /> -->
      </SelectPopover>
    </div>

    <div>
      <slot name="logic" />
    </div>

    <div>
      <Btn color="ghost" icon @click="$emit('remove')" data-test-id="filter-remove">
        <DeleteIcon16 />
      </Btn>
    </div>
  </div>
</template>

<script lang="ts">
// TODO: display truncated code pills (give pill min-width)
import { defineComponent, PropType, Component } from 'vue';

import { contains, filter, map } from 'ramda';
import Selector from './Selector.vue';
import { valueTypeByMode } from './valueTypeByMode';
import { HydratedFilter, Mode, ValueType } from '@/filter/types';
import { AnyEntity, ProjectCode } from '@/types';
import ChevronDownIcon16 from '@components/Icons/ChevronDownIcon16.vue';
import CodeIcon16 from '@components/Icons/CodeIcon16.vue';
import DeleteIcon16 from '@components/Icons/DeleteIcon16.vue';
import ModeSelector from './ModeSelector.vue';
import SelectItem from '@components/SelectItem.vue';
import CodeSelectItem from '@components/CodeSelect/CodeSelectItem.vue';
import CodeOccurrenceBubble from '@components/CodeSelect/CodeOccurrenceBubble.vue';
import GroupIcon16 from '@components/Icons/GroupIcon16.vue';
import DocumentIcon16 from '@components/Icons/DocumentIcon16.vue';
import UserIcon16 from '@components/Icons/UserIcon16.vue';
import FilterValueDisplay from './FilterValueDisplay.vue';
import type { ListGroups } from '@components/common/SelectionList.vue';

import { getTextsForFilterSelect } from '@/filter/getTextsForFilterSelect';

export default defineComponent({
  name: 'FilterRow',
  emits: { remove: null, update: null },

  components: {
    ChevronDownIcon16,
    CodeIcon16,
    DeleteIcon16,
    ModeSelector,
    Selector,
    SelectItem,
    GroupIcon16,
    DocumentIcon16,
    UserIcon16,
    CodeOccurrenceBubble,
    CodeSelectItem,
    FilterValueDisplay,
  },

  props: {
    filter: {
      type: Object as PropType<HydratedFilter>,
      required: true,
    },

    items: {
      type: Array as PropType<Array<AnyEntity>>,
      required: false,
      default: () => [],
    },

    optionGroups: {
      type: Object as PropType<ListGroups>,
      default: () => ({}),
    },

    /**
     * Component to render for each selected item in the button that toggles
     * the selection list
     */
    buttonComponent: {
      type: String,
      required: false,
      default: 'CodePill',
    },

    /**
     * the component to show in the selection list for item filters.
     *
     * CodeSelectItem is an extra component, all other values use SelectItem with
     * the itemComponent as the specified icon / component
     */
    itemComponent: {
      type: String as PropType<'GroupIcon' | 'DocumentIcon' | 'ColorDot' | 'CodeSelectItem'>,
      required: false,
      default: null,
    },

    getDisplayText: {
      type: Function,
      default: null,
    },
  },

  computed: {
    itemOptions(): Array<{ label: AnyEntity; value: string; group?: string }> {
      return map((item: AnyEntity) => ({ label: item, value: item.id, group: item.optionGroup }), this.items);
    },

    selectedItems(): Array<AnyEntity> {
      if (!Array.isArray(this.filter.value.value)) {
        return [];
      }

      return filter((item) => contains(item.id, this.filter.value.value as Array<string>), this.items);
    },

    hasSelectedItems(): boolean {
      return this.filter.value.value ? Boolean(this.filter.value.value.length) : false;
    },

    valueType(): ValueType {
      return valueTypeByMode(this.filter.value.mode);
    },

    useSingleSelect(): boolean {
      const modeConfig = this.filter.allowedModes?.[this.filter.value.mode];

      return this.valueType === 'id-list' && modeConfig === 'singleSelect';
    },

    value(): string | Array<string> {
      if (this.useSingleSelect) {
        return this.filter.value.value ? this.filter.value.value[0] : '';
      }

      return this.filter.value.value || [];
    },

    isDisabled(): Boolean {
      return this.filter.value.mode === 'hasnothing';
    },

    selectComponent(): Component {
      return this.itemComponent === 'CodeSelectItem' ? CodeSelectItem : SelectItem;
    },

    /**
     * Every filter can define specific texts based on
     * the filter mode and name, but most just use the default value
     *
     * For searchEmpty, there's also a global default for
     * all filters.
     *
     */
    texts(): {
      empty: string;
      placeholder: string;
      searchPlaceholder: string;
      searchEmpty: string;
    } {
      const name = this.filter.name;
      const mode = this.filter.value.mode;

      return getTextsForFilterSelect(name, mode);
    },
  },

  methods: {
    setMode(mode: Mode) {
      const newModeConfig = this.filter.allowedModes?.[mode];

      const updateData: { mode: Mode; value?: string | Array<string> } = {
        mode,
      };

      if (valueTypeByMode(mode) === this.valueType) {
        if (this.valueType === 'id-list' && newModeConfig === 'singleSelect' && this.filter.value.value) {
          // If we go from multi select to single select, only keep the first value
          updateData.value = [this.filter.value.value[0]];
        } else {
          // else, keep value for text, or id-list with same select
          updateData.value = this.filter.value.value;
        }
      }

      if (mode === 'hasnothing') {
        // If hasnothing is selected, the default value is an empty array
        updateData.value = [];
      }

      this.$emit('update', updateData);
    },

    setValue(value: Array<string>) {
      this.$emit('update', {
        ...this.filter.value,
        value,
      });
    },

    editCode(code: ProjectCode) {
      this.$store.dispatch('modal/show', {
        type: 'codeEdit',
        stackable: false,
        payload: { code },
      });
    },
  },
});
</script>

<style lang="scss">
.filter-row {
  display: grid;
  grid-template-columns: 200px 200px 214px 80px 40px;
  grid-gap: 8px;
}

.filter-row__mode-button {
  width: 100%;
}

.filter-row button.multi-select__button > div {
  grid-template-columns: auto min-content;
  justify-content: normal;
  text-align: left;
}

.filter-row__list {
  display: grid;
  grid-auto-flow: column;
  grid-gap: 4px;
  justify-content: start;
  align-items: center;
}
</style>
